// ─── Pipeline (cumulative reach) ──────────────────────────────────────
// Replaces v1's status funnel. Each stage is the count of suggestions that
// EVER reached that point — strictly monotonically decreasing — so the
// percentages are honest. Closed cell tinted green (terminal positive).
// Rejection lane sits in its own row with distinct red-dashed treatment.

import { useNavigate } from "react-router-dom";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { statusColor } from "../../components/StatusChip";
import { useWorkflowConfig } from "../../hooks/useWorkflow";

export interface PipelineStage {
  label: string;
  count: number;
  to?: string;
  terminal?: boolean;
  /**
   * Status that the stage represents. Drives the card's color theming
   * via the workflow yaml's category palette — same source of truth
   * the StatusChip uses, so the funnel reads in family with the chips
   * elsewhere on the page.
   */
  representativeStatus?: string;
}

// Per-state rejection bucket. Pipeline renders one pill per entry so the
// rejected lane scales with however many `rejected`-category states the
// workflow yaml defines.
export interface RejectionBucket {
  status: string;
  label: string;
  count: number;
  to?: string;
}

interface PipelineProps {
  stages: PipelineStage[];
  total: number;
  rejections: RejectionBucket[];
}

export default function Pipeline({ stages, total, rejections }: PipelineProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { data: workflow } = useWorkflowConfig();
  const totalRejected = rejections.reduce((sum, r) => sum + r.count, 0);
  const rejectRate =
    total > 0 ? Math.round((totalRejected * 100) / total) : 0;
  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="overline" color="text.secondary">
          Pipeline · Cumulative Reach
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Each stage = suggestions that ever reached this point
        </Typography>
      </Stack>
      <Box
        sx={{
          display: "grid",
          // One row per stage count at lg; three-up below so a six-stage
          // happy path never wraps five-plus-one.
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            sm: "repeat(3, minmax(0, 1fr))",
            lg: `repeat(${Math.max(stages.length, 1)}, minmax(0, 1fr))`,
          },
          gap: 2,
        }}
      >
        {stages.map((s) => {
          const interactive = !!s.to;
          const pct = total > 0 ? Math.round((s.count * 100) / total) : 0;
          // Color triple from the workflow category. Falls back to
          // copper-warm when no representative status is provided
          // (preserves the v1 visual for stages without a clear status
          // mapping). For semantic clarity the bg is a soft tint and
          // the number takes the dot color.
          const colors = s.representativeStatus
            ? statusColor(workflow, s.representativeStatus, theme.palette.mode)
            : null;
          const restBg = colors
            ? alpha(colors.dot, 0.08)
            : alpha(theme.palette.primary.main, 0.04);
          const hoverBg = colors
            ? alpha(colors.dot, 0.14)
            : alpha(theme.palette.primary.main, 0.08);
          const numberColor = colors ? colors.dot : theme.palette.text.primary;
          return (
            <Box
              key={s.label}
              role={interactive ? "button" : undefined}
              tabIndex={interactive ? 0 : undefined}
              onClick={interactive ? () => navigate(s.to!) : undefined}
              onKeyDown={
                interactive
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(s.to!);
                      }
                    }
                  : undefined
              }
              sx={{
                bgcolor: restBg,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "12px",
                p: 1.75,
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
                cursor: interactive ? "pointer" : "default",
                transition: "background-color .15s",
                "&:hover": interactive ? { bgcolor: hoverBg } : undefined,
                "&:focus-visible": interactive
                  ? {
                      outline: "2px solid",
                      outlineColor: colors ? colors.dot : "primary.main",
                      outlineOffset: 2,
                    }
                  : undefined,
              }}
            >
              <Typography
                variant="tableHeader"
                sx={{ color: "text.primary" }}
              >
                {s.label}
              </Typography>
              <Typography
                variant="h4"
                sx={{ lineHeight: 1.1, color: numberColor }}
              >
                {s.count}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {pct}% of submitted
              </Typography>
            </Box>
          );
        })}
      </Box>

      {totalRejected > 0 && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{
            mt: "24px !important",
            p: 1.5,
            bgcolor: alpha(theme.palette.error.main, 0.06),
            border: "1px dashed",
            borderColor: alpha(theme.palette.error.main, 0.3),
            borderRadius: "12px",
            flexWrap: "wrap",
            rowGap: 1,
          }}
        >
          <Typography
            variant="overline"
            sx={{ color: "error.main", letterSpacing: "1.5px", lineHeight: 1 }}
          >
            Rejected Lane
          </Typography>
          {rejections
            .filter((r) => r.count > 0)
            .map((r) => (
              <Box
                key={r.status}
                role={r.to ? "button" : undefined}
                tabIndex={r.to ? 0 : undefined}
                onClick={r.to ? () => navigate(r.to!) : undefined}
                onKeyDown={
                  r.to
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(r.to!);
                        }
                      }
                    : undefined
                }
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  color: "error.main",
                  borderRadius: 999,
                  px: 1.75,
                  py: 0.75,
                  cursor: r.to ? "pointer" : "default",
                  "&:hover": r.to
                    ? { bgcolor: alpha(theme.palette.error.main, 0.18) }
                    : undefined,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "error.main",
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {r.label} ({r.count})
                </Typography>
              </Box>
            ))}
          <Typography
            variant="caption"
            color="text.primary"
            sx={{ ml: { sm: "auto" } }}
          >
            {totalRejected} total · {rejectRate}% rejection rate
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
